import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { PasswordInput } from "../components/PasswordInput.js";

type Status = "probing" | "invalid" | "ready" | "done";

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [status, setStatus] = useState<Status>("probing");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordError =
    newPassword.length > 0 && newPassword.length < 8
      ? "Password must be at least 8 characters."
      : newPassword.length > 0 && confirmPassword.length > 0 && newPassword !== confirmPassword
        ? "Passwords do not match."
        : null;

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiFetch<{ valid: boolean }>(
          `/api/auth/password-reset/status?token=${encodeURIComponent(token)}`
        );
        if (cancelled) return;
        setStatus(res.valid ? "ready" : "invalid");
      } catch {
        if (!cancelled) setStatus("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (passwordError || newPassword.length < 8) return;
    setLoading(true);
    setError(null);
    try {
      await apiFetch<{ ok: true }>("/api/auth/reset-password", {
        method: "POST",
        body: { token, new_password: newPassword }
      });
      setStatus("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="centerPage">
      <div className="card pageNarrow wFull">
        {status === "probing" && <div className="muted">Checking link…</div>}

        {status === "invalid" && (
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>Invalid link</div>
            <div className="muted" style={{ marginBottom: 16 }}>
              This password reset link is invalid or has expired. Request a new one from the sign-in page.
            </div>
            <Link to="/login" className="btn btnPrimary">
              Sign in
            </Link>
          </div>
        )}

        {status === "ready" && (
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>Choose a new password</div>
            <div className="help" style={{ marginBottom: 12 }}>Minimum 8 characters.</div>
            <form onSubmit={(e) => void onSubmit(e)} style={{ display: "grid", gap: 10 }}>
              <label className="field">
                <span className="label">New password</span>
                <PasswordInput value={newPassword} onChange={setNewPassword} autoComplete="new-password" aria-label="New password" />
              </label>
              <label className="field">
                <span className="label">Confirm password</span>
                <PasswordInput
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  autoComplete="new-password"
                  aria-label="Confirm new password"
                />
              </label>
              {passwordError ? <div className="muted">{passwordError}</div> : null}
              <button
                type="submit"
                disabled={loading || !newPassword || !confirmPassword || passwordError != null}
                className="btn btnPrimary"
              >
                Update password
              </button>
            </form>
          </div>
        )}

        {status === "done" && (
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>Password updated</div>
            <div className="muted" style={{ marginBottom: 16 }}>You can sign in with your new password.</div>
            <Link to="/login" className="btn btnPrimary">
              Sign in
            </Link>
          </div>
        )}

        {error && status === "ready" && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 800, color: "var(--danger-text)", marginBottom: 4 }}>Error</div>
            <div className="muted">{error}</div>
          </div>
        )}
      </div>
    </div>
  );
}
